import React, { useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { Row, Col, Label, FormGroup, Button, UncontrolledPopover, PopoverBody, Input } from "reactstrap";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Accordion } from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { TbLoader } from "react-icons/tb";
import LogoImage from "src/assets/images/logo-icon.svg";
import Dropzone from "react-dropzone";
import { reloadOrganizationActiveModuleSettingsFromServer } from "src/store/actions";
import ComplaintCustomizationService from "src/apis/ComplaintCustomizationService";
import WhistleblowingCustomizationService from "src/apis/WhistleblowingCustomizationService";
import LogoEditorModal from "./logo-editor-modal";
import { IMAGE_ACCEPT_TYPES } from "src/common/constants";

const CompanyLogoCustomization = (props) => {
  const { t, fetchOrganizationDetails, reloadActiveModuleSettings, modules } = props;
  const details = fetchOrganizationDetails.data;
  const [cropModal, setCropModal] = useState({
    isOpen: false,
    type: null,
    file: null,
  });
  const defaultSizes = {
    colored: Number(details?.primary_logo_size),
    white: Number(details?.secondary_logo_size),
    reduced: Number(details?.small_logo_size),
  };
  
  const logoSpecs = {
    colored: { formKey: "primaryLogo", sizeKey: "primaryLogoSize", aspect: { w: 4, h: 1 }, defaultSize: defaultSizes.colored },
    white: { formKey: "secondaryLogo", sizeKey: "secondaryLogoSize", aspect: { w: 4, h: 1 }, defaultSize: defaultSizes.white },
    reduced: { formKey: "smallLogo", sizeKey: "smallLogoSize", aspect: { w: 1, h: 1 }, defaultSize: defaultSizes.reduced },
  };
  
  const [logoSizes, setLogoSizes] = useState({
    colored: logoSpecs.colored.defaultSize,
    white: logoSpecs.white.defaultSize,
    reduced: logoSpecs.reduced.defaultSize,
  });
  
  const [logos, setLogos] = useState({
    colored: {
      serverUrl: typeof details?.primary_logo === "string" ? details.primary_logo : null,
      originalFile: null,
      processedFile: null,
      previewUrl: typeof details?.primary_logo === "string" ? details.primary_logo : null,
    },
    white: {
      serverUrl: typeof details?.secondary_logo === "string" ? details.secondary_logo : null,
      originalFile: null,
      processedFile: null,
      previewUrl: typeof details?.secondary_logo === "string" ? details.secondary_logo : null,
    },
    reduced: {
      serverUrl: typeof details?.small_logo === "string" ? details.small_logo : null,
      originalFile: null,
      processedFile: null,
      previewUrl: typeof details?.small_logo === "string" ? details.small_logo : null,
    },
  });
  
  
  /*********************************** FUNCTIONS ***************************************** */
  const getLogoPreviewUrl = (type) => {
    const item = logos[type];
    return item?.previewUrl || item?.serverUrl || null;
  };
  
  // Submit Information - API call
  const handleSubmitMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
    (["colored", "white", "reduced"]).forEach((type) => {
      const spec = logoSpecs[type];
      const item = logos[type];
      const fileToSend = item?.processedFile || item?.originalFile;
  
      if (fileToSend) {
        formData.append(spec.formKey, fileToSend);
      }
      
      formData.append(spec.sizeKey, String(logoSizes[type] ?? spec.defaultSize ?? ""));
    });
  

      const service = modules.active === "cm" ? ComplaintCustomizationService.getInstance() : WhistleblowingCustomizationService.getInstance();
      return await service.editLogos(formData);
    },
    onSuccess: () => {
      reloadActiveModuleSettings();
      fetchOrganizationDetails.refetch();
      toast(t("Data saved successfully."), {
        type: "success",
      });
    },
    onError: () => {
      toast(t("Failed to save data."), {
        type: "error",
      });
    },
  });

  // Restore Information
  const restore = () => {
    setLogos({
      colored: {
        serverUrl: typeof details?.primary_logo === "string" ? details.primary_logo : null,
        originalFile: null,
        processedFile: null,
        previewUrl: typeof details?.primary_logo === "string" ? details.primary_logo : null,
      },
      white: {
        serverUrl: typeof details?.secondary_logo === "string" ? details.secondary_logo : null,
        originalFile: null,
        processedFile: null,
        previewUrl: typeof details?.secondary_logo === "string" ? details.secondary_logo : null,
      },
      reduced: {
        serverUrl: typeof details?.small_logo === "string" ? details.small_logo : null,
        originalFile: null,
        processedFile: null,
        previewUrl: typeof details?.small_logo === "string" ? details.small_logo : null,
      },
    });
  
    setLogoSizes({
      colored: Number(details?.primary_logo_size),
      white: Number(details?.secondary_logo_size),
      reduced: Number(details?.small_logo_size),
    });
  };

  const setLogoFile = (type, file) => {
    const previewUrl = URL.createObjectURL(file);
  
    setLogos((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        originalFile: prev[type]?.originalFile, // keep if you want
        processedFile: file, // <-- THIS is key
        previewUrl,
      },
    }));
  };
    
    const closeCropModal = () => {
      setCropModal((prev) => {
        if (prev.previewUrl && prev.previewUrl.startsWith("blob:")) {
          try { URL.revokeObjectURL(prev.previewUrl); } catch (_) {}
        }
        return { isOpen: false, type: null, file: null, previewUrl: null, reason: "" };
      });
    };

    const handleApplyCropped = (processedFile) => {
      const type = cropModal.type;
      if (!type) return;
    
      const previewUrl = URL.createObjectURL(processedFile);
    
      setLogos((prev) => {
        const prevPreview = prev?.[type]?.previewUrl;
        if (prevPreview && prevPreview.startsWith("blob:")) {
          try { URL.revokeObjectURL(prevPreview); } catch (_) {}
        }
    
        return {
          ...prev,
          [type]: {
            ...prev[type],
            processedFile: processedFile,  // <-- key
            previewUrl,
          },
        };
      });
    
      // Close modal state
      setCropModal({ isOpen: false, type: null, file: null, previewUrl: null, reason: "", aspect: null });
    };

  // Submiting files
  const handleAcceptedFiles = async (type, acceptedFiles) => {
      const file = acceptedFiles?.[0];
      if (!file) return;

      const spec = logoSpecs[type];
      if (!spec) {
        throw new Error(`Invalid logo type: ${type}`);
      }

      setCropModal({
        isOpen: true,
        type,
        file,
      });
  };

  /*************************************** VIEW ****************************************** */
  return (
    <Row>
      <LogoEditorModal cropModal={cropModal} closeCropModal={closeCropModal} setLogoFile={setLogoFile} onApply={handleApplyCropped} />
      <div className="page-customization">
        <Row>
          <Col className="d-flex align-items-start customization-image-col">
            <img src={LogoImage} alt="Logo" />
          </Col>
          <Col className="customization-accordion-col">
            <Accordion defaultActiveKey="0">
              <Accordion.Item
                className="border-none cusom-accordion-item customization-accordion"
                eventKey="0"
              >
                <Accordion.Header className="custom-accordion-header">
                  <div className="p-1 custom-div-header">
                    {`4. ${t("Logo")}`}{" "}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                    <Row className="mb-4">
                        {/* Colored Logo */}
                        <Col lg="6" md="12" sm="12" className="mb-3">
                            <FormGroup className="d-flex justify-content-between align-items-end mb-3">
                                <div style={{width: '80%'}}>
                                    <Label for="fontFamily">
                                        {t("Colored Logo") + ":"}

                                        <span id="colored-logo-info-btn" className="section-info-button">
                                            <i className="ri-information-line" />
                                        </span>
                                        <UncontrolledPopover
                                            placement="bottom"
                                            trigger="hover"
                                            target="colored-logo-info-btn"
                                        >
                                            <PopoverBody>
                                                <span>
                                                    {t("This image will be displayed in the sign in, register, set password and reset password pages")}
                                                </span>
                                            </PopoverBody>
                                        </UncontrolledPopover>
                                    </Label>
                                    <Dropzone
                                        accept={IMAGE_ACCEPT_TYPES}
                                        onDrop={(acceptedFiles) =>
                                            handleAcceptedFiles("colored", acceptedFiles)
                                        }
                                        maxFiles={1}
                                        multiple={false}
                                        >
                                        {({ getRootProps, getInputProps }) => (
                                            <div className="dropzone tpdd p-3">
                                                <div
                                                    className="dz-message needsclick"
                                                    {...getRootProps()}>
                                                    <input {...getInputProps()} />
                                                    <button type="button" className="btn select-files-btn">
                                                        {props.t('Select file...')}
                                                    </button>
                                                    <h4>
                                                        {props.t(
                                                            "Drop file here or click to upload"
                                                        )}
                                                    </h4>
                                                </div>
                                            </div>
                                        )}
                                    </Dropzone>
                                </div>
                                <div className="dropzone-previews tpdd">
                                    <img 
                                        src={getLogoPreviewUrl("colored")}
                                        alt="" 
                                    />
                                </div>
                            </FormGroup>
                        </Col>

                        {/* White Transparent Logo */}
                        <Col lg="6" md="12" sm="12" className="mb-3">
                            <FormGroup className="d-flex justify-content-between align-items-end mb-3">
                                <div style={{width: '80%'}}>
                                    <Label for="fontFamily">
                                        {t("White Transparent Logo") + ":"}
                                        <span id="white-transparent-logo-info-btn" className="section-info-button">
                                            <i className="ri-information-line" />
                                        </span>

                                        <UncontrolledPopover
                                            placement="bottom"
                                            trigger="hover"
                                            target="white-transparent-logo-info-btn"
                                        >
                                            <PopoverBody>
                                                <span>
                                                    {t("This image will be displayed in the left side of your panel as the main logo.")}
                                                </span>
                                            </PopoverBody>
                                        </UncontrolledPopover>
                                    </Label>
                                    <Dropzone
                                        accept={IMAGE_ACCEPT_TYPES}
                                        onDrop={(acceptedFiles) =>
                                            handleAcceptedFiles("white", acceptedFiles)
                                        }
                                        maxFiles={1}
                                        multiple={false}
                                        >
                                        {({ getRootProps, getInputProps }) => (
                                            <div className="dropzone tpdd p-3">
                                                <div
                                                    className="dz-message needsclick"
                                                    {...getRootProps()}>
                                                    <input {...getInputProps()} />
                                                    <button type="button" className="btn select-files-btn">
                                                        {props.t('Select file...')}
                                                    </button>
                                                    <h4>
                                                        {props.t(
                                                            "Drop file here or click to upload"
                                                        )}
                                                    </h4>
                                                </div>
                                            </div>
                                        )}
                                    </Dropzone>
                                </div>

                                <div className="dropzone-previews primary-background tpdd">
                                    <img 
                                        src={getLogoPreviewUrl("white")} 
                                        alt="" 
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        {/* Reduced Menu Icon */}
                        <Col lg="6" md="12" sm="12" className="mb-3">
                            <FormGroup className="d-flex justify-content-between align-items-end mb-3">
                                <div style={{width: '80%'}}>
                                    <Label for="fontFamily">
                                        {t("Reduced Menu Icon") + ":"}

                                        <span id="reduced-logo-info-btn" className="section-info-button">
                                            <i className="ri-information-line" />
                                        </span>

                                        <UncontrolledPopover
                                            placement="bottom"
                                            trigger="hover"
                                            target="reduced-logo-info-btn"
                                        >
                                            <PopoverBody>
                                                <span>
                                                    {t("This image will be displayed in the left side of your panel as the main logo when the menu is collapsed.")}
                                                </span>
                                            </PopoverBody>
                                        </UncontrolledPopover>
                                    </Label>
                                    <Dropzone
                                        accept={IMAGE_ACCEPT_TYPES}
                                        onDrop={(acceptedFiles) =>
                                            handleAcceptedFiles("reduced", acceptedFiles)
                                        }
                                        maxFiles={1}
                                        multiple={false}
                                        >
                                        {({ getRootProps, getInputProps }) => (
                                            <div className="dropzone tpdd p-3">
                                                <div
                                                    className="dz-message needsclick"
                                                    {...getRootProps()}>
                                                    <input {...getInputProps()} />
                                                    <button type="button" className="btn select-files-btn">
                                                        {props.t('Select file...')}
                                                    </button>
                                                    <h4>
                                                        {props.t(
                                                            "Drop file here or click to upload"
                                                        )}
                                                    </h4>
                                                </div>
                                            </div>
                                        )}
                                    </Dropzone>
                                </div>
                                
                                <div className="dropzone-previews primary-background tpdd">
                                    <img 
                                        src={getLogoPreviewUrl("reduced")}
                                        alt="" 
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>
                </Accordion.Body>
                <Row className="p-4">
                  <Col sm="12" className="d-flex justify-content-end">
                    <Button
                      onClick={restore}
                      color="primary"
                      type="button"
                      outline
                      className="me-2"
                      disabled={
                        handleSubmitMutation.isLoading ||
                        fetchOrganizationDetails.isLoading ||
                        fetchOrganizationDetails.isFetching
                      }
                    >
                      {t("Restore")}
                    </Button>

                    <Button
                      color="primary"
                      onClick={handleSubmitMutation.mutate}
                      type="submit"
                      disabled={
                        handleSubmitMutation.isLoading ||
                        fetchOrganizationDetails.isLoading ||
                        fetchOrganizationDetails.isFetching
                      }
                    >
                      {handleSubmitMutation.isLoading ? <TbLoader /> : t("Save")}
                    </Button>
                  </Col>
                </Row>
              </Accordion.Item>
            </Accordion>
          </Col>
        </Row>
      </div>
    </Row>
  );
};

const mapStatetoProps = (state) => {
  const { token } = state.Login;
  const organization = state.Organization;
  const App = state.App;
  const modules = state.Modules;
  return { organization, token, App, modules };
};

const mapDispachToProps = (dispach) => {
  return {
    reloadActiveModuleSettings: () =>
      dispach(reloadOrganizationActiveModuleSettingsFromServer()),
  };
};

export default withNamespaces()(
  withRouter(
    connect(
      mapStatetoProps,
      mapDispachToProps
    )(CompanyLogoCustomization)
  )
);
